import { TFunction } from "i18next";
import { AuthgardAdminAction } from "src/common/enums";
import { humanizeMfaStatus } from "src/common/util";
import { isInviteExpired } from "src/hooks/use-bulk-manage-scan2auth";
import type { BulkUser, IneligibilityReason } from "src/hooks/use-bulk-manage-scan2auth";

/**
 * Shared copy and styling for the bulk credential-management wizard's steps. The label helpers take
 * `t` as their first argument rather than closing over it, matching humanizeMfaStatus and the other
 * shared humanizers in common/util — so any step can call them without threading a bound copy.
 */

/**
 * Class that turns Alchemy's `action` variant — the filled primary — red, for a destructive confirm.
 * Alchemy has no destructive variant, so the styling lives in index.scss and works through the custom
 * properties that variant resolves; see the rule there for why that is the only route into its shadow
 * root, and why the token names have to be the variant-specific ones.
 *
 * Kept as a constant so the class name has one home alongside the wizard's other shared copy, rather
 * than being spelled out at each call site.
 */
export const DESTRUCTIVE_BUTTON_CLASS = 'btn-destructive';

/**
 * Whether an action needs the destructive treatment: the warning banner, the error-alert styling, and
 * the type-to-confirm gate. Resetting MFA locks users out until they re-enroll and cancelling revokes
 * pending invites; initiating an enrollment is additive, so it isn't destructive.
 */
export const isDestructiveAction = (action: AuthgardAdminAction): boolean =>
    action === AuthgardAdminAction.REREGISTER ||
    action === AuthgardAdminAction.CANCEL_INITIATE;

/** The operator-facing name of an action. */
export const actionLabel = (t: TFunction, action: AuthgardAdminAction): string => {
    switch (action) {
        case AuthgardAdminAction.INITIATE: return t('bulk-manage-credentials-action-initiate-label');
        case AuthgardAdminAction.REREGISTER: return t('bulk-manage-credentials-action-reset-label');
        case AuthgardAdminAction.CANCEL_INITIATE: return t('bulk-manage-credentials-action-cancel-label');
        default: return '';
    }
};

/** The one-line explanation shown under an action's name on the select step. */
export const actionDescription = (t: TFunction, action: AuthgardAdminAction): string => {
    switch (action) {
        case AuthgardAdminAction.INITIATE: return t('bulk-manage-credentials-action-initiate-desc');
        case AuthgardAdminAction.REREGISTER: return t('bulk-manage-credentials-action-reset-desc');
        case AuthgardAdminAction.CANCEL_INITIATE: return t('bulk-manage-credentials-action-cancel-desc');
        default: return '';
    }
};

/**
 * A cohort member's current MFA state, via the shared humanizer so status copy is identical in the
 * single-user modal, the bulk wizard, and the principal list. Expired-ness is judged at the
 * wizard's pinned instant (UseBulkManageScan2AuthResult.nowMs) so the label always matches the
 * partition the operator is being shown, not a clock that moved since.
 */
export const userStatusLabel = (t: TFunction, user: BulkUser, nowMs: number): string =>
    humanizeMfaStatus(t, user.currentStatus, isInviteExpired(user, nowMs));

/**
 * What to show against an ineligible principal. A status mismatch reads as their current state (the
 * operator can act on it later); a credential-type mismatch is about the principal, so it gets its own
 * copy rather than a state that would look misleadingly actionable.
 */
export const ineligibleLabel = (t: TFunction, user: BulkUser, reason: IneligibilityReason, nowMs: number): string =>
    reason === 'credential-type'
        ? t('bulk-manage-credentials-reason-other-credential-type')
        : userStatusLabel(t, user, nowMs);
